import React, { useMemo } from "react";
import {
  Modal,
  Alert,
  Text,
  NumberInput,
  Group,
  Stack,
} from "@mantine/core";
import { IconSettings } from "@tabler/icons";
import moment from "moment";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "./pageList";
import { useFormRender } from "../hooks/useFormRender";
import { useCellRender } from "../hooks/useCellRender";
import { useFormatter } from "../hooks/useFomatter";
import { useServerApi } from "../hooks/userServerApi";
import RemoteSelect2 from "./remoteSelect2";
import CommissionFileImporter from "./commissionFileImporter";
import AutoCodeInput from "./autoCodeInput";
import FormConfirmation from "./formConfirmation";
import { TextInput } from "@mantine/core";

const BankTransactionCreateCommissionStatement = ({
  opened,
  onClose,
  bankTransaction,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const [formatter] = useFormatter();

  const formSchema = useMemo(
    () => ({
      title: t("bankTransaction.createCommissionStatement.title"),
      display: {
        mode: ENUM_FORM_DISPLAY.INSIDE,
        size: "xl",
      },
      showSaveBar: false,
      apiEntity: "commissionStatement",
      initialValues: {
        status: "PENDING",
        rows: [],
        total: 0,
        verified: 0,
        missing: 0,
        policyNotFound: 0,
        notMatch: 0,
        period: moment().format("YYYYMM"),
        bankCharge: bankTransaction?.bankCharge || 0,
      },
      validate: {
        period: (value) =>
          value?.length !== 6
            ? t("bankTransaction.createCommissionStatement.periodFormat")
            : null,
      },
      layout: {
        containers: [
          {
            key: "t1",
            parent: null,
            type: ENUM_FORM_LAYOUT_CONTAINER.WIZARD,
            props: {
              variant: "default",
              defaultValue: "Basic Info",
              orientation: "vertical",
              style: { minHeight: "400px" },
            },
            steps: [
              { key: "t1-1", label: "Basic Info", icon: IconSettings },
              { key: "t1-2", label: "Import File", icon: IconSettings },
              { key: "t1-3", label: "Confirm", icon: IconSettings },
            ],
          },
          {
            key: "section-info",
            parent: "t1-1",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: t("bankTransaction.createCommissionStatement.basicInfo"),
              withBorder: false,
              ml: 20,
              mt: 0,
            },
          },
          {
            key: "section-import",
            parent: "t1-2",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: t("bankTransaction.createCommissionStatement.importFile"),
              withBorder: false,
              ml: 20,
              mt: 0,
            },
          },
          {
            key: "section-confirm",
            parent: "t1-3",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: t("Confirm"),
              withBorder: false,
              ml: 20,
              mt: 0,
            },
          },
        ],
        fields: [
          {
            name: "code",
            component: AutoCodeInput,
            parent: "section-info",
            props: {
              required: true,
              placeholder: "IMP-CMS-202205",
              label: "Patch ID",
            },
          },
          {
            name: "provider",
            component: RemoteSelect2,
            parent: "section-info",
            props: {
              name: "provider",
              apiEntity: "provider",
              required: true,
              label: "Provider",
              placeholder: "AIA",
              valueField: "_id",
              labelField: "name",
              searchFields: ["name", "code"],
              sort: { by: "name", order: "desc" },
            },
          },
          {
            name: "period",
            component: TextInput,
            parent: "section-info",
            props: {
              placeholder: "202212",
              label: "Period",
            },
          },
          {
            name: "importRemark",
            component: TextInput,
            parent: "section-info",
            props: {
              placeholder: "",
              label: "Remark",
            },
          },
          {
            name: "bankCharge",
            component: NumberInput,
            parent: "section-info",
            props: {
              label: t("bankTransaction.bankCharge"),
              precision: 2,
              parser: formatter.currencyParser,
              formatter: formatter.currencyFormat,
              min: 0,
            },
          },
          {
            name: "rows",
            component: CommissionFileImporter,
            parent: "section-import",
            props: {},
          },
          {
            name: "confirm",
            component: FormConfirmation,
            parent: "section-confirm",
            props: {},
          },
        ],
      },
    }),
    [bankTransaction, formatter, t]
  );

  const handleSubmit = async ({ values }) => {
    if (!bankTransaction?._id) return;
    try {
      await api.CommissionStatement.createWithBankTransaction({
        ...values,
        bankTransactionId: bankTransaction._id,
      });
      toast.success(t("bankTransaction.createCommissionStatement.success"));
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(
        error?.message || t("bankTransaction.createCommissionStatement.error")
      );
      throw error;
    }
  };

  const [renderForm, formAction, formStatus] = useFormRender(
    formSchema,
    () => {
      onSuccess?.();
      onClose();
    },
    handleSubmit
  );

  React.useEffect(() => {
    if (opened && !formStatus.isOpen) {
      formAction.open({ mode: "add" });
    }
    if (!opened && formStatus.isOpen) {
      formAction.close();
    }
  }, [opened]);

  if (!bankTransaction) return null;

  const netAmount = +(bankTransaction.deposit || 0).toFixed(2);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("bankTransaction.createCommissionStatement.title")}
      size="xl"
    >
      <Stack spacing="md">
        <Alert color="blue" variant="light">
          <Text size="sm" weight={600}>
            {t("bankTransaction.createCommissionStatement.linkedDeposit")}:{" "}
            {bankTransaction.code}
          </Text>
          <Group spacing="lg" mt="xs">
            <Text size="xs">
              {moment(bankTransaction.date).format("LL")}
            </Text>
            <Text size="xs">
              {t("bankTransaction.linkCommissionStatement.netDeposit")}:{" "}
              {cellRender.Currency(netAmount)}
            </Text>
          </Group>
        </Alert>
        {renderForm()}
      </Stack>
    </Modal>
  );
};

export default BankTransactionCreateCommissionStatement;
